import { DesignerInterface } from "../../../../base/DesignerInterface";
import { Image, Panel } from "../../../..";
import { SearchFilterMode } from "./SearchFilterPanel";
import { Table } from "../../Table";
import { Textbox } from "../../../textbox/Textbox";
import { TextboxProps } from "../../../textbox/TextboxProps";
import { TextboxVariant } from "../../../textbox/TextboxVariant";

/**
 * This class defines basic logic for the Search/Filter fields in the table header.  There are two modes:
 * - Expanded: When the expanded mode is in use, the field displays its image (the magnifying glass or the funnel)
 *   inside the textbox (as the imagePre).
 * - Collapsed: When the collapsed mode is in use, the image is always rendered.  Clicking the image shows/hides the
 *   textbox (to the left of the image).
 */
export abstract class AbstractSearchFilterTextboxPanel extends Panel {
    protected image: Image;
    protected readonly table: Table;
    protected textbox: Textbox;
    protected textboxProps: Partial<TextboxProps>;
    private readonly transitionMillis = 175;

    constructor(table: Table, props?: Partial<TextboxProps>) {
        super({ margin: 0, padding: 0, rowBreak: false, wrap: false, _designer: table._designer });
        this.table = table;
        this.textboxProps = props;
        this.recreateFields();
        this.allowDropInDesigner = false;
        this._shouldAddDesignerContainerProperties = false;
    }

    get(): Textbox {
        return this.textbox;
    }

    private recreateFields() {
        this.removeAll();
        this.createTextbox();
        this.createImage();
        if (this.table.searchFilterMode === SearchFilterMode.EXPANDED) {
            this.image.removeClickListener(this.toggleCollapsedView);
            this.textbox.imagePre = this.image;
            this.add(this.textbox)
        }
        else {
            this.textbox.imagePre = null;
            this.image.addClickListener(this.toggleCollapsedView);
            this.add(this.image);
        }
    }

    syncFields(visible: boolean, recreateFields: boolean) {
        if (visible) {
            if (this.isEmpty() || recreateFields)
                this.recreateFields();
        }
        else
            this.hide();
    }

    protected createTextbox() {
        this.textbox = new Textbox({
            ...this.textboxProps,
            fontSize: 16,
            captionVisible: false,
            variant: TextboxVariant.UNDERLINED,
            rowBreak: false,
            _designer: this._designer
        });
        delete this.textboxProps;
    }

    protected createImage() {
        this.image = new Image({
            paddingLeft: 4,
            height: 28,
            width: 28,
            rowBreak: false,
            _designer: this._designer
        });
    }

    private applyTransitionCSS() {
        this.textbox.style.transition = `width ${this.transitionMillis}ms ease-in`;
    }

    private removeTransitionCSS() {
        this.textbox.style.transition = "";
    }

    protected isExpanded(): boolean {
        return this.components.includes(this.textbox);
    }

    protected displayTextbox() {
        if (!this.contains(this.textbox)) {
            this.textbox.width = 0;
            this.insert(this.textbox, 0);
            this.textbox.focus();
            requestAnimationFrame(() => {
                this.applyTransitionCSS();
                this.textbox.width = null;
                setTimeout(() => {
                    this.removeTransitionCSS();
                }, this.transitionMillis);
            });
        }
    }

    protected removeTextbox() {
        if (this.contains(this.textbox)) {
            requestAnimationFrame(() => {
                this.applyTransitionCSS();
                this.textbox.width = 0;
                setTimeout(() => {
                    this.remove(this.textbox);
                    this.removeTransitionCSS();
                    this.textbox.width = null;
                }, this.transitionMillis);
            });
        }
    }

    protected hide() {
        this.removeAll();
    }

    optionIsAvailable(): boolean {
        return !this.isEmpty();
    }

    override focus() {
        if (this.contains(this.textbox))
            this.textbox.focus();
        else if (this.contains(this.image))
            this.image.focus();
    }

    protected toggleCollapsedView = () => {
        if (this.contains(this.textbox))
            this.removeTextbox();
        else
            this.displayTextbox();
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        this.textbox._designer = value;
        this.image._designer = value;
    }
}
